import { useEffect } from 'react';
import { NoiseEngine } from '../audio/noiseEngine';
import type { WhiteNoiseSettings } from '../types';

// Single shared engine for the whole app (white noise persists across navigation).
let engine: NoiseEngine | null = null;
function getEngine(): NoiseEngine {
  if (!engine) engine = new NoiseEngine();
  return engine;
}

/** Play the end-of-session chime from anywhere (shares the singleton engine). */
export function playAlarmSound(volume = 0.8) {
  const e = getEngine();
  void e.resume();
  e.playAlarm(volume);
}

/**
 * Drives ambient white-noise playback from settings using the procedural
 * Web Audio engine — no remote files, no network, fully reliable. Mount once
 * near the app root so sound keeps playing while navigating.
 */
export function useAudio(whiteNoiseSettings: WhiteNoiseSettings) {
  // Browsers block audio until a user gesture — resume the context on the first one.
  useEffect(() => {
    const resume = () => { void getEngine().resume(); };
    window.addEventListener('pointerdown', resume);
    window.addEventListener('keydown', resume);
    return () => {
      window.removeEventListener('pointerdown', resume);
      window.removeEventListener('keydown', resume);
    };
  }, []);

  useEffect(() => {
    const e = getEngine();
    if (whiteNoiseSettings.enabled) {
      void e.resume();
      e.applyMix(whiteNoiseSettings.sounds);
    } else {
      e.stopAll();
    }
  }, [whiteNoiseSettings]);

  return {
    playAlarm: (volume = 0.8) => getEngine().playAlarm(volume),
    previewSound: (id: string, volume = 0.5) => {
      const e = getEngine();
      void e.resume();
      e.setSound(id as any, volume);
      setTimeout(() => e.setSound(id as any, 0), 2500);
    },
  };
}
